import MuiModal from '@mui/material/Modal'
import { styled } from '../../utils/styled'
import Grid from '../Grid'
import { type CSSObject } from '../theme/themeTypes'

// Fixed rather than a caller-supplied pixel value, so every modal that opts
// into a blurred backdrop looks the same.
const BACKDROP_BLUR_PX = 8

export const StyledModal = styled(MuiModal, {
  shouldForwardProp: (prop) => prop !== 'blurBackdrop'
})<{ blurBackdrop: boolean }>(
  ({ blurBackdrop }): CSSObject => ({
    animation: 'kurama-modal-fade-in 150ms ease',
    '@keyframes kurama-modal-fade-in': {
      from: { opacity: 0 },
      to: { opacity: 1 }
    },
    // The scrim colour is the app-wide `MuiBackdrop` override from the theme, so
    // every modal surface dims the page the same way; only the blur is per-modal.
    '& .MuiBackdrop-root': {
      backdropFilter: blurBackdrop ? `blur(${BACKDROP_BLUR_PX}px)` : 'none'
    }
  })
)

/**
 * Fills the viewport and lays its children out in a column. The surface itself
 * is transparent — the scrim is the backdrop behind it — so a caller can put
 * a full-bleed layout here, or centre a panel of its own.
 *
 * `MuiModal` doesn't render this for you: it expects exactly one root element
 * as its child, onto which it attaches `ref`/`tabIndex` for its own focus
 * trap, but it doesn't size, position, or lay that element out — an unstyled
 * child would render at its intrinsic size in the corner of the screen. This
 * is that required root element, and it owns the full-viewport sizing, the
 * flex layout for children, and the `role="dialog"`/`aria-*` attributes that
 * `MuiModal` also leaves for the child to carry.
 */
export const StyledSurface = styled(Grid)(() => ({
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  outline: 'none'
}))
